import { Calendar, Cancel } from '@eduzz/houston-icons';
import SearchOutline from '@eduzz/houston-icons/SearchOutline';
import Button from '@eduzz/houston-ui/Button';
import { FormHelperText } from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormLabel from '@material-ui/core/FormLabel';
import Grid from '@material-ui/core/Grid';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import clsx from 'clsx';
import { Field, Form as FormikForm } from 'formik';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { DateRangePickerFormField } from 'v2/components/Shared/FormFields/DateRangePicker';
import { SelectFormField } from 'v2/components/Shared/FormFields/SelectFormField';
import TextFormField from 'v2/components/Shared/FormFields/TextFormField';
import { sendEventToGA } from 'v2/helpers/analytics';
import { useMutableOptions } from 'v2/hooks/useMutableOptions';
import { MovementsType, SearchType, TransactionsType } from 'v2/interfaces/financial';
import { financialStatementService } from 'v2/services/financialStatement/statement';

import type { FinancialStatementFilter, EFinancialStatementTabs } from 'v2/interfaces/financial';

import { TransactionTypesFilter, MovementTypesFilter, SearchTypeFilter, PresetRanges, calendarPreset } from './constants';
import useStyles from './styles';

import type { FormikProps } from 'formik';
import type { Moment } from 'moment';

interface IFormProps extends FormikProps<FinancialStatementFilter> {
  currentTab: EFinancialStatementTabs;
}

const translateFieldName = (field: string) => {
  if (field === 'periodPreset') return 'Últimos Lançamentos';
  if (field === 'movement_type') return 'Movimentações';
  if (field === 'transaction_type') return 'Tipo da Transação';
  return 'Busque por';
};

function Form({ initialValues, values, handleChange, currentTab, setFieldValue, errors }: IFormProps) {
  const classes = useStyles();

  const isOutsideRange = (day: Moment) => day.isAfter(moment()) || day.isAfter(moment(values.dates[0], 'DD/MM/YYYY').add(180, 'days'));

  const [movementTypeFilter, setMovementTypeFilter] = useState<MovementsType>(MovementsType.ALL);
  const [oldValues, setOldValues] = useState<FinancialStatementFilter>(initialValues);
  const [isSearchSent, setIsSearchSent] = useState<boolean>(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const handleToggleCalendarOpen = () => {
    if (!isStatementTab) return;
    if (!isCalendarOpen) {
      sendEventToGA({
        category: 'Novo Extrato',
        action: 'Filtro',
        label: 'PesquisaPorPeriodo',
      });
    }
    setIsCalendarOpen((isOpen) => !isOpen);
  };

  const isStatementTab = currentTab === 'statement';

  const transactionTypesFilter = useMutableOptions({
    options: TransactionTypesFilter,
    callback: (option) => movementTypeFilter === MovementsType.ALL || option.type.includes(movementTypeFilter),
  });

  const transactionEqualMovementType = transactionTypesFilter.filter((transaction) => transaction.value === values.transaction_type);

  const handleChangeMovementTypeFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMovementTypeFilter(event.target.value as MovementsType);
  };

  const handleSearch = () => {
    financialStatementService.setFilter(values);
    setIsSearchSent(true);
  };

  const PlaceholderForSearchType = (search_type: SearchType) => {
    const SearchTypeSelected = SearchTypeFilter.find((searchTypeValue) => searchTypeValue.value === search_type);
    return SearchTypeSelected?.placeholder;
  };

  useEffect(() => {
    if (isStatementTab) {
      if (transactionEqualMovementType.length === 0) {
        setFieldValue('transaction_type', TransactionsType.ALL);
      }

      if (moment(values.dates[1], 'DD/MM/YYYY').isSameOrBefore(moment(values.dates[0], 'DD/MM/YYYY').add(179, 'days'))) {
        financialStatementService.setFilter({
          periodPreset: values.periodPreset,
          dates: values.dates,
          movement_type: values.movement_type,
          transaction_type: values.transaction_type,
        });
      }

      setOldValues(values);

      Object.keys(values).forEach((field, index) => {
        const oldValue = values[field];
        const newValue = Object.entries(oldValues)[index][1];

        if (oldValue !== newValue) {
          sendEventToGA({
            category: 'Novo Extrato',
            action: 'Filtro',
            label: translateFieldName(field[0]),
          });
        }
      });
    } else {
      setFieldValue('transaction_type', TransactionsType.ALL);
      setFieldValue('movement_type', MovementsType.ALL);
    }
  }, [values, isStatementTab, setFieldValue, oldValues, transactionEqualMovementType.length]);

  useEffect(() => {
    if (values.search_text === '' && isSearchSent) {
      financialStatementService.setFilter({
        ...values,
        search_type: SearchType.ALL,
      });
      setIsSearchSent(false);
    }
  }, [isSearchSent, values, values.search_text]);

  useEffect(() => {
    if (values.search_type === SearchType.ALL && !errors.dates) {
      setFieldValue('search_text', '');
    }
  }, [setFieldValue, values.search_type, errors.dates]);

  useEffect(() => {
    setFieldValue('dates', [
      moment()
        .subtract(Number(values.periodPreset) - 1, 'days')
        .format('DD/MM/YYYY'),
      moment().format('DD/MM/YYYY'),
    ]);
  }, [setFieldValue, values.periodPreset, isCalendarOpen]);

  useEffect(() => {
    if (!isStatementTab) {
      setIsCalendarOpen(false);
    }
  }, [isStatementTab]);

  return (
    <FormikForm>
      <div id='financial-filter'>
        <Grid container spacing={2} alignItems='flex-start'>
          <Grid item xs={12} md={6}>
            <FormControl style={{ width: '100%' }}>
              <div className={classes.labelContainer}>
                <FormLabel className={classes.label} id='periodPreset'>
                  Últimos lançamentos
                </FormLabel>
                {errors.dates && <FormHelperText error>{errors.dates}</FormHelperText>}
              </div>
              <RadioGroup
                className={classes.presetDates}
                aria-labelledby='periodPreset'
                defaultValue={initialValues.periodPreset}
                value={values.periodPreset}
                name='periodPreset'
                onChange={handleChange}
              >
                {!isCalendarOpen &&
                  PresetRanges.map((preset) => (
                    <FormControlLabel
                      key={preset.label}
                      className={clsx(
                        classes.presetItem,
                        preset.value === values?.periodPreset && isStatementTab && '--active',
                        !isStatementTab && '--disabled'
                      )}
                      control={<Radio />}
                      value={preset.value}
                      label={preset.label}
                      disabled={!isStatementTab}
                    />
                  ))}

                {isCalendarOpen && (
                  <div className={classes.calendar}>
                    <Field
                      id='calendar'
                      label=''
                      name='dates'
                      customPreset={calendarPreset}
                      component={DateRangePickerFormField}
                      isOutsideRange={isOutsideRange}
                      maxDate={moment()}
                    />
                  </div>
                )}

                <div
                  className={`${classes.calendarIcon} ${isCalendarOpen && '--closeCalendar'} ${!isStatementTab && '--disabled'}`}
                  onClick={handleToggleCalendarOpen}
                >
                  {isCalendarOpen ? <Cancel size={20} /> : <Calendar size={20} />}
                </div>
              </RadioGroup>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <Field
              id='movement_type'
              name='movement_type'
              label='Movimentações'
              options={MovementTypesFilter}
              component={SelectFormField}
              inputProps={{ onChange: handleChangeMovementTypeFilter }}
              disabled={!isStatementTab}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <Field
              id='transaction_type'
              name='transaction_type'
              label='Tipo da transação'
              options={transactionTypesFilter}
              component={SelectFormField}
              disabled={!isStatementTab}
              fullWidth
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} className={classes.advancedFields}>
          <Grid item xs={12} md={3}>
            <Field id='search_type' name='search_type' label='Busque por' options={SearchTypeFilter} component={SelectFormField} fullWidth />
          </Grid>

          <Grid item xs={12} md={7}>
            <Field
              id='search_text'
              name='search_text'
              label='&nbsp;'
              placeholder={PlaceholderForSearchType(values.search_type) || 'Digite os dados da sua busca'}
              mask={values.search_type === SearchType.DOCUMENT ? 'document' : null}
              component={TextFormField}
              fullWidth
              disabled={values.search_type === SearchType.ALL}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            &nbsp;
            <Button
              style={{ height: 'unset' }}
              startIcon={<SearchOutline />}
              variant='contained'
              fullWidth
              disabled={values.search_type === SearchType.ALL || !values.search_text || !!errors.search_text}
              onClick={handleSearch}
            >
              Buscar
            </Button>
          </Grid>
        </Grid>
      </div>
    </FormikForm>
  );
}

export default Form;
